import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/components/ui/sidebar';
import { extractErrorMessage } from '@/modules/shared/errorHandling';
import { useDebouncedValue } from '@/modules/shared/hooks/useDebouncedValue';
import { useTranslation } from '@/modules/shared/i18n/useTranslation';
import ResultsPanel from './components/ResultsPanel';
import { SEARCH_DEBOUNCE_MS } from './constants';
import { useGlobalSearchQuery } from './queries';
import { en, th } from './locales';
import { testIds } from '@/test/testIds';

/**
 * Portal-wide search mounted in the left navigation. Typing fires the search
 * API (debounced); results render in a full-height panel covering the whole
 * content area right of the sidebar. Closing (✕ / ESC / outside click) keeps
 * the last query + results; collapsed sidebar shows an icon that expands the
 * rail and focuses the input.
 */
const GlobalSearch: React.FC = () => {
  const { t } = useTranslation({ en, th });
  const { state, setOpen: setSidebarOpen, isMobile } = useSidebar();
  const isCollapsed = !isMobile && state === 'collapsed';

  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [panelLeft, setPanelLeft] = useState('16rem'); // fallback = sidebar's default --sidebar-width
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const debouncedTerm = useDebouncedValue(term, SEARCH_DEBOUNCE_MS).trim();
  const { data, isFetching, error } = useGlobalSearchQuery(debouncedTerm);

  useEffect(() => {
    if (error) toast.error(extractErrorMessage(error));
  }, [error]);

  // Collapsing the rail unmounts the input; drop the panel with it.
  useEffect(() => {
    if (isCollapsed) setOpen(false);
  }, [isCollapsed]);

  // The panel is portaled to <body> (the sidebar's fixed z-10 container is a
  // stacking context, so anything nested inside it paints below the page's
  // sticky table headers). --sidebar-width doesn't inherit onto <body>, so
  // resolve it from the provider when the panel opens.
  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const width = getComputedStyle(anchorRef.current).getPropertyValue('--sidebar-width').trim();
    if (width) setPanelLeft(width);
  }, [open]);

  // ESC and clicks outside the panel/input dismiss the panel. The panel is a
  // plain fixed div (not a Radix popover), so dismissal is handled here.
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleExpandClick = () => {
    setSidebarOpen(true);
    setTimeout(() => inputRef.current?.focus(), 200); // allow sidebar expand animation
  };

  const handleChange = (value: string) => {
    setTerm(value);
    setOpen(Boolean(value.trim()));
  };

  const handleClear = () => {
    setTerm('');
    setOpen(false);
    inputRef.current?.focus();
  };

  if (isCollapsed) {
    return (
      <button
        type="button"
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-background',
          'shadow-sm transition hover:shadow-md focus:outline-none',
        )}
        onClick={handleExpandClick}
        aria-label={t('expandSearch')}
        data-testid={testIds.globalSearch.collapsedTrigger}
      >
        <Search className="h-5 w-5 text-muted-foreground" />
      </button>
    );
  }

  return (
    <>
      <div ref={anchorRef} className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          className={cn(
            'w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-xl border border-border',
            'bg-background py-2 pl-10 pr-9 text-sm placeholder-muted-foreground focus:outline-none',
          )}
          placeholder={t('searchPlaceholder')}
          type="text"
          value={term}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => term.trim() && setOpen(true)}
          data-testid={testIds.globalSearch.input}
        />
        {term && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={t('clear')}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground',
              'transition-colors hover:text-foreground',
            )}
            data-testid={testIds.globalSearch.clear}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{ left: panelLeft }}
            className={cn(
              'fixed inset-y-0 right-0 z-[95] overflow-y-auto',
              'border-l border-border bg-background p-6',
              'animate-in fade-in-0 slide-in-from-left-2 duration-150',
            )}
            data-testid={testIds.globalSearch.overlay}
          >
            <div className="mx-auto max-w-[1200px]">
              <ResultsPanel
                results={data}
                isFetching={isFetching}
                hasSearched={debouncedTerm.length > 0}
                onClose={() => setOpen(false)}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default GlobalSearch;
